import React from 'react';
import { Box, Typography, Grid, Card, CardContent, Chip, Stack } from '@mui/material';

import linenProducts from '../../data/linenProducts.json';

/**
 * "What you actually get" — the hospitality linen inventory shown as real product
 * photography, grouped by category.
 *
 * Product shots arrive on mixed backgrounds (white cutouts, grey, styled lifestyle),
 * so images are rendered with objectFit:'contain' on a neutral tile. That keeps the
 * grid visually even and, more importantly, never crops a product.
 *
 * @param {Array} categories - defaults to src/data/linenProducts.json
 */
const LinenProductGallery = ({ categories = linenProducts.categories }) => (
  <Box>
    {categories.map((category) => (
      <Box key={category.id} sx={{ mb: { xs: 4, md: 6 }, '&:last-of-type': { mb: 0 } }}>
        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2.5 }}>
          <Chip
            label={category.label}
            color="primary"
            size="small"
            sx={{ fontWeight: 700 }}
          />
          <Box sx={{ flexGrow: 1, height: '1px', bgcolor: 'divider' }} />
        </Stack>

        <Grid container spacing={{ xs: 2, md: 3 }}>
          {category.products.map((product) => (
            <Grid item xs={12} sm={6} md={4} key={product.id}>
              <Card
                elevation={0}
                sx={{
                  height: '100%',
                  borderRadius: 3,
                  border: '1px solid',
                  borderColor: 'divider',
                  overflow: 'hidden',
                  display: 'flex',
                  flexDirection: 'column',
                  transition: 'border-color 0.3s ease, box-shadow 0.3s ease',
                  '&:hover': { borderColor: 'primary.main', boxShadow: 3 },
                }}
              >
                <Box
                  component="img"
                  src={product.image}
                  alt={product.alt}
                  loading="lazy"
                  decoding="async"
                  sx={{
                    width: '100%',
                    aspectRatio: '1 / 1',
                    objectFit: 'contain',
                    objectPosition: 'center',
                    display: 'block',
                    bgcolor: 'grey.50',
                    p: 1,
                  }}
                />
                <CardContent sx={{ p: 2.5, flexGrow: 1 }}>
                  <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                    {product.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                    {product.spec}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>
    ))}
  </Box>
);

export default LinenProductGallery;
